/**
 * A "What's new" note opens by itself ONCE — the next time the person opens
 * the app, iPad or desktop (mockup B2). One note per app open; a second one
 * waits for next time. Showing it marks it opened, so it never opens by
 * itself again; it stays in Tasks until they tap Got it. No push, no bell.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { RecipientNote } from "@/lib/whats-new";
import { NoteView } from "./note-view";

/** Once per page load, whatever remounts. */
let shownThisLoad = false;
export function __resetWhatsNewAutoOpen() {
    shownThisLoad = false;
}

export function WhatsNewAutoOpen() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [note, setNote] = useState<RecipientNote | null>(null);
    const q = useQuery({
        queryKey: ["whats-new", "next", user?.id],
        queryFn: () => api.get<{ note: RecipientNote | null }>("/api/whats-new/next"),
        enabled: !!user && !shownThisLoad,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });
    useEffect(() => {
        const next = q.data?.note;
        if (!next || shownThisLoad) return;
        shownThisLoad = true;
        setNote(next);
        api.post(`/api/whats-new/tasks/${next.taskId}/opened`).catch(() => {});
    }, [q.data]);

    const gotIt = useMutation({
        mutationFn: (taskId: string) => api.post(`/api/whats-new/tasks/${taskId}/got-it`),
        onSuccess: () => {
            setNote(null);
            void qc.invalidateQueries({ queryKey: ["tasks"] });
            void qc.invalidateQueries({ queryKey: ["task-inbox"] });
        },
        onError: () => toast.error("Couldn't save that. Try again."),
    });

    const closeRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!note) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setNote(null);
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [note]);

    if (!note) return null;
    return createPortal(
        <div className="fixed inset-0 z-[96] grid place-items-center overflow-y-auto bg-black/55 p-3 sm:p-6">
            <div
                role="dialog"
                aria-modal="true"
                aria-label={note.title}
                className="relative w-full max-w-[760px] rounded-3xl bg-card p-5 text-card-foreground shadow-2xl sm:p-7"
                data-testid="whats-new-auto"
            >
                <button
                    ref={closeRef}
                    type="button"
                    onClick={() => setNote(null)}
                    className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-muted-foreground hover:bg-secondary"
                    aria-label="Close. It stays in your Tasks."
                    title="It stays in your Tasks"
                >
                    <X className="h-6 w-6" aria-hidden />
                </button>
                <NoteView
                    title={note.title}
                    steps={note.steps}
                    senderName={note.senderName}
                    sentAt={note.sentAt}
                    busy={gotIt.isPending}
                    onGotIt={() => gotIt.mutate(note.taskId)}
                />
            </div>
        </div>,
        document.body,
    );
}
